import React, { useState, useRef, useCallback, useEffect } from 'react';

export type TrendDataPoint = {
  eventId?: string;
  completedAt: string;
  percentage: number;
  part: string;
  name: string;
};

type SeriesConfig = {
  key: string;
  label: string;
  color: string;
  dotColor: string;
};

const SERIES: SeriesConfig[] = [
  { key: 'listening',   label: '听力', color: '#4F46E5', dotColor: '#4338CA' },
  { key: 'reading',     label: '阅读', color: '#10B981', dotColor: '#059669' },
  { key: 'writing',     label: '书写', color: '#8B5CF6', dotColor: '#7C3AED' },
  { key: 'simulation',  label: '总分', color: '#F59E0B', dotColor: '#D97706' },
];

const PAD = { top: 16, right: 24, bottom: 36, left: 44 };
const H = 200;
const PLOT_H = H - PAD.top - PAD.bottom;
const Y_TICKS = [0, 20, 40, 60, 80, 100];
const MAX_POINTS = 20;

function toY(pct: number) {
  return PAD.top + PLOT_H - (pct / 100) * PLOT_H;
}

function buildPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(' ');
}

/** Simple OLS linear regression on (index, percentage) pairs. */
function linearRegression(percentages: number[]): { m: number; b: number } | null {
  const n = percentages.length;
  if (n < 3) return null;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) {
    sumX  += i;
    sumY  += percentages[i];
    sumXY += i * percentages[i];
    sumXX += i * i;
  }
  const denom = n * sumXX - sumX * sumX;
  if (Math.abs(denom) < 1e-9) return null;
  const m = (n * sumXY - sumX * sumY) / denom;
  const b = (sumY - m * sumX) / n;
  return { m, b };
}

type TooltipState = {
  x: number;
  y: number;
  label: string;
  value: number;
  color: string;
} | null;

interface Props {
  data: TrendDataPoint[];
  family?: 'HSK' | 'CSCA';
}

const ScoreTrendChart: React.FC<Props> = ({ data, family = 'HSK' }) => {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<TooltipState>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const resize = () => setWidth(Math.max(260, Math.floor(container.clientWidth)));
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const plotWidth = width - PAD.left - PAD.right;
  const pointX = (index: number, total: number) => total <= 1
    ? PAD.left + plotWidth / 2 : PAD.left + (index / (total - 1)) * plotWidth;

  const toggle = useCallback((key: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  // All series share exam positions on the x axis. A section missing from one
  // exam leaves a gap rather than shifting later section scores to the left.
  const pointKey = (point: TrendDataPoint) => point.eventId || `${point.completedAt}:${point.name}`;
  const axisEvents = [...new Map(data.map(point => [pointKey(point), point.completedAt])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1]))
    .slice(-MAX_POINTS);
  const axisIndex = new Map(axisEvents.map(([id], index) => [id, index]));
  const xFor = (point: TrendDataPoint) => pointX(axisIndex.get(pointKey(point)) || 0, axisEvents.length);
  const seriesPoints = (family === 'CSCA' ? [{ ...SERIES[3], label: 'CSCA 成绩' }] : SERIES).map((s) => {
    const pts = data
      .filter((d) => {
        const p = String(d.part || '').toLowerCase();
        return axisIndex.has(pointKey(d)) && p === s.key;
      })
      .sort((a, b) => xFor(a) - xFor(b));
    return { ...s, pts };
  });

  // All non-empty visible series
  const activeSeries = seriesPoints.filter((s) => s.pts.length > 0 && !hidden.has(s.key));

  // Linear trend line — only when exactly one series is visible
  const trendInfo = (() => {
    if (activeSeries.length !== 1) return null;
    const s = activeSeries[0];
    if (s.pts.length < 3) return null;
    const reg = linearRegression(s.pts.map(pt => pt.percentage));
    if (!reg) return null;
    const n = s.pts.length;
    const x1 = xFor(s.pts[0]);
    const x2 = xFor(s.pts[n - 1]);
    // Clamp regression y-values to chart bounds [0, 100]
    const y1Raw = reg.m * 0 + reg.b;
    const y2Raw = reg.m * (n - 1) + reg.b;
    const y1 = toY(Math.max(0, Math.min(100, y1Raw)));
    const y2 = toY(Math.max(0, Math.min(100, y2Raw)));
    const label = reg.m > 1.5 ? '↑ 上升趋势' : reg.m < -1.5 ? '↓ 下降趋势' : '→ 稳定';
    return { x1, y1, x2, y2, label, color: s.color };
  })();

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg) return;

      const ctm = svg.getScreenCTM()?.inverse();
      if (!ctm) return;
      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const { x: svgX, y: svgY } = point.matrixTransform(ctm);

      if (svgX < PAD.left - 4 || svgX > PAD.left + plotWidth + 4) {
        setTooltip(null);
        return;
      }
      let best: TooltipState = null;
      let bestDist = Infinity;

      activeSeries.forEach((s) => {
        s.pts.forEach((pt) => {
          const x = xFor(pt);
          const y = toY(pt.percentage);
          const dist = Math.hypot(svgX - x, svgY - y);
          if (dist < bestDist && dist < 24) {
            bestDist = dist;
            best = { x, y, label: pt.name, value: pt.percentage, color: s.color };
          }
        });
      });
      setTooltip(best);
    },
    [activeSeries]
  );

  const hasAnyData = seriesPoints.some((s) => s.pts.length > 0);

  if (!hasAnyData) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        暂无成绩数据
      </div>
    );
  }

  return (
    <div className="w-full" ref={containerRef}>
      {/* Legend toggles */}
      <div className="flex flex-wrap gap-2 mb-3">
        {seriesPoints
          .filter((s) => s.pts.length > 0)
          .map((s) => {
            const isHidden = hidden.has(s.key);
            return (
              <button
                key={s.key}
                onClick={() => toggle(s.key)}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                  isHidden
                    ? 'border-gray-200 text-gray-400 bg-white'
                    : 'border-transparent text-white'
                }`}
                style={isHidden ? {} : { backgroundColor: s.color }}
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: isHidden ? '#D1D5DB' : 'white' }}
                />
                {s.label}（{s.pts.length}）
              </button>
            );
          })}
      </div>

      {/* SVG chart */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${H}`}
        className="w-full"
        style={{ height: 200 }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* Grid lines + Y labels */}
        {Y_TICKS.map((tick) => {
          const y = toY(tick);
          return (
            <g key={tick}>
              <line
                x1={PAD.left}
                y1={y}
                x2={PAD.left + plotWidth}
                y2={y}
                stroke={tick === 0 ? '#9CA3AF' : '#E5E7EB'}
                strokeWidth={tick === 0 ? 1.5 : 1}
              />
              <text
                x={PAD.left - 6}
                y={y + 4}
                textAnchor="end"
                fontSize={10}
                fill="#9CA3AF"
              >
                {tick}%
              </text>
            </g>
          );
        })}

        {/* Linear trend line (single visible series only, ≥3 pts) */}
        {trendInfo && (
          <g>
            <line
              x1={trendInfo.x1}
              y1={trendInfo.y1}
              x2={trendInfo.x2}
              y2={trendInfo.y2}
              stroke={trendInfo.color}
              strokeWidth={1.5}
              strokeDasharray="10 5"
              opacity={0.4}
            />
            {/* Trend label near the right end */}
            <text
              x={trendInfo.x2 - 4}
              y={trendInfo.y2 - 7}
              textAnchor="end"
              fontSize={10}
              fontWeight="bold"
              fill={trendInfo.color}
              opacity={0.75}
            >
              {trendInfo.label}
            </text>
          </g>
        )}

        {/* Series lines + dots */}
        {activeSeries.map((s) => {
          const points = s.pts.map((pt) => ({
            x: xFor(pt),
            y: toY(pt.percentage),
          }));
          return (
            <g key={s.key}>
              <path
                d={buildPath(points)}
                fill="none"
                stroke={s.color}
                strokeWidth={2.5}
                strokeLinejoin="round"
                strokeLinecap="round"
                opacity={0.9}
              />
              {points.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={4}
                  fill="white"
                  stroke={s.dotColor}
                  strokeWidth={2}
                ><title>{`${s.label} · ${s.pts[i].name}: ${s.pts[i].percentage}%`}</title></circle>
              ))}
            </g>
          );
        })}

        {/* X axis label */}
        <text
          x={PAD.left + plotWidth / 2}
          y={H - 4}
          textAnchor="middle"
          fontSize={10}
          fill="#9CA3AF"
        >
          最近 {axisEvents.length} 次（从左到右由旧到新）
        </text>

        {/* Tooltip vertical line + bubble */}
        {tooltip && (
          <g>
            <line
              x1={tooltip.x}
              y1={PAD.top}
              x2={tooltip.x}
              y2={PAD.top + PLOT_H}
              stroke={tooltip.color}
              strokeWidth={1}
              strokeDasharray="4 3"
              opacity={0.6}
            />
            {(() => {
              const bw = 140;
              const bh = 40;
              const bx = Math.min(
                Math.max(tooltip.x - bw / 2, PAD.left),
                PAD.left + plotWidth - bw
              );
              const by = tooltip.y < PAD.top + bh + 8 ? tooltip.y + 12 : tooltip.y - bh - 8;
              return (
                <g>
                  <rect
                    x={bx}
                    y={by}
                    width={bw}
                    height={bh}
                    rx={6}
                    fill="white"
                    stroke={tooltip.color}
                    strokeWidth={1.5}
                    filter="drop-shadow(0 2px 4px rgba(0,0,0,.1))"
                  />
                  <text x={bx + 8} y={by + 15} fontSize={10} fill="#6B7280">
                    {tooltip.label.length > 20 ? tooltip.label.slice(0, 20) + '…' : tooltip.label}
                  </text>
                  <text x={bx + 8} y={by + 31} fontSize={13} fontWeight="bold" fill={tooltip.color}>
                    {tooltip.value}%
                  </text>
                </g>
              );
            })()}
          </g>
        )}
      </svg>
    </div>
  );
};

export default ScoreTrendChart;
